import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getNotifications } from "../services/api";
import {
  LayoutDashboard, FilePlus2, ClipboardList, Gem, Bell, User, Users,
  CheckCircle2, BarChart3, Settings, LogOut, Menu, X,
} from "lucide-react";

const NAV = {
  Employee: [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/app/request/new", label: "Submit Request", icon: FilePlus2 },
    { to: "/app/requests", label: "My Requests", icon: ClipboardList },
    { to: "/app/gems", label: "My Gems", icon: Gem },
    { to: "/app/notifications", label: "Notifications", icon: Bell },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  Manager: [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/app/requests", label: "Requests", icon: ClipboardList },
    { to: "/app/users", label: "My Team", icon: Users },
    { to: "/app/gems", label: "Gems Activity", icon: Gem },
    { to: "/app/notifications", label: "Notifications", icon: Bell },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  HR: [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/app/requests", label: "Approval Queue", icon: ClipboardList },
    { to: "/app/users", label: "All Users", icon: Users },
    { to: "/app/gems", label: "Gems Management", icon: Gem },
    { to: "/app/reports", label: "Reports", icon: BarChart3 },
    { to: "/app/notifications", label: "Notifications", icon: Bell },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  Director: [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/app/requests", label: "Final Approvals", icon: CheckCircle2 },
    { to: "/app/users", label: "All Users", icon: Users },
    { to: "/app/gems", label: "Gems Reports", icon: Gem },
    { to: "/app/reports", label: "Analytics", icon: BarChart3 },
    { to: "/app/settings", label: "Settings", icon: Settings },
    { to: "/app/notifications", label: "Notifications", icon: Bell },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
};

function SidebarContent({ user, unread, onNav }) {
  const items = NAV[user.role] || NAV.Employee;
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pt-6 pb-5">
        <div className="font-display text-2xl font-extrabold text-vault-cream tracking-tight">The Vault</div>
        <div className="text-[11px] uppercase tracking-widest text-vault-accent font-semibold mt-1">Clark Academy</div>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={onNav}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-vault-accent text-vault-panel"
                    : "text-vault-subtle hover:bg-vault-panel3 hover:text-vault-cream"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" />
              <span className="flex-1">{it.label}</span>
              {it.label === "Notifications" && unread > 0 && (
                <span className="rounded-full bg-vault-accent text-vault-panel text-[10px] font-bold px-1.5 py-0.5 min-w-[18px] text-center">
                  {unread}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 mt-2">
        <div className="rounded-2xl bg-vault-panel2 p-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-vault-accent text-vault-panel grid place-items-center font-bold text-sm">
            {user.firstName[0]}{user.lastName[0]}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-vault-cream truncate">{user.firstName} {user.lastName}</div>
            <div className="text-[11px] text-vault-subtle truncate">{user.role} · {user.primaryLocation}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, doLogout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    getNotifications().then((list) => {
      if (active) setUnread((list || []).filter((n) => !n.isRead).length);
    }).catch(() => {});
    return () => { active = false; };
  }, [location.pathname]);

  if (!user) return null;
  const handleLogout = () => { doLogout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-vault-bg">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-vault-panel flex-col z-30">
        <SidebarContent user={user} unread={unread} />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-vault-panel">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-vault-subtle">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent user={user} unread={unread} onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 bg-vault-bg/85 backdrop-blur border-b border-black/10">
          <div className="flex items-center justify-between px-4 sm:px-6 h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => setOpen(true)} className="lg:hidden text-vault-panel">
                <Menu className="h-6 w-6" />
              </button>
              <div className="font-display font-bold text-vault-panel text-lg lg:hidden">The Vault</div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/app/notifications")}
                className="relative h-10 w-10 grid place-items-center rounded-full bg-vault-panel text-vault-cream hover:bg-vault-panel2 transition-colors"
              >
                <Bell className="h-[18px] w-[18px]" />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-vault-accent text-vault-panel text-[10px] font-bold grid place-items-center">
                    {unread}
                  </span>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-full bg-vault-panel text-vault-cream px-4 h-10 text-sm font-semibold hover:bg-vault-panel2 transition-colors"
              >
                <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
